import { useAuth } from "@clerk/clerk-react";
import { Box, CircularProgress, Fab } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useRef, useState } from "react";
import { unstable_usePrompt } from "react-router-dom";
import { BentoCategory, OrganizationProfile } from "schemas/dashboard";
import { Map } from "schemas/functions";

import { LocationAutocompleteHandle } from "components/LocationAutocomplete";
import { PAGE_VISITED } from "constants/trackingProps";
import AboutForm from "features/Auth/Onboarding/AboutForm";
import IncomeForm from "features/Auth/Onboarding/IncomeForm";
import EngagementForm from "features/Influencer/Profile/EngagementForm";
import PartnershipForm from "features/Influencer/Profile/PartnershipForm";
import RecommendationForm from "features/Influencer/Profile/RecommendationForm";
import SignatureForm from "features/Influencer/Profile/SignatureForm";
import StandOutForm from "features/Influencer/Profile/StandOutForm";
import { fetcherAuth } from "utils/api";
import { removeDuplicates } from "utils/object";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import DetailedCategoryForm from "./DetailedCategoryForm";
import Introduction from "./Introduction";
import styles from "./styles";

const Profile = () => {
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const {
    currentUser,
    setCurrentUser,
    currentOrg,
    setCurrentOrg,
    profile,
    setProfile,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);
  const [loading, setLoading] = useState(false);
  const { tabWidth, discoverTab } = useContext(DiscoverViewContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );

  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [isDirty, setIsDirty] = useState(false);
  const [needsUpdateCategory, setNeedsUpdateCategory] = useState(false);
  const locationAutocompleteRef = useRef<LocationAutocompleteHandle>(null);

  const categories = currentOrg?.bentoCategories;

  const [excludeIncludeFilterHasChanged, setExcludeIncludeFilterHasChanged] =
    useState(false);

  const saveOnboardingForm = async () => {
    let formToSave: Map = onboardingForm;
    // Always check the location input — state may be stale if the user edited
    // the prefilled text without fully clearing it (which never invalidates
    // state via handleInputChange).
    const result = await locationAutocompleteRef.current?.resolveLocation();
    if (result?.resolved?.country_code) {
      formToSave = {
        ...formToSave,
        city: result.resolved.address,
        location: result.resolved.country_code,
        addressComponents: result.resolved.address_components,
        latitude: result.resolved.latitude,
        longitude: result.resolved.longitude,
      };
      setOnboardingForm(formToSave);
    } else if (result?.hasText && !result?.valid) {
      setAlert(
        "Please select a location from the dropdown suggestions",
        "error",
      );
      return;
    }

    const unfilledRequireFields = validateRequiredFields(formToSave);
    if (unfilledRequireFields?.length > 0) {
      setAlert(
        `Please fill out required fields in ${unfilledRequireFields?.join(
          ", ",
        )}`,
        "warning",
      );
      return;
    }
    if (!categories || categories?.length === 0) {
      setAlert("Please select at least one category", "warning");
      return;
    }

    if (excludeIncludeFilterHasChanged) {
      handleSaveFilters();
    }

    try {
      if (needsUpdateCategory) {
        const cleanCategories = removeDuplicates(categories, "id");
        updateCategory(cleanCategories);
      }
      setLoading(true);
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        { ...formToSave, page: "Profile" },
      );
      setProfile((prev) => ({
        ...prev,
        ...organizationProfile,
      }));
      setCurrentUser((prev) => {
        if (prev) {
          prev.name = formToSave.name;
        }
        return prev;
      });
      setAlert("Successfully updated profile", "success");
      setIsDirty(false);
    } catch (error) {
      if (error?.message?.includes("location")) {
        setOnboardingForm((prev) => ({
          ...prev,
          location: undefined,
          latitude: undefined,
          longitude: undefined,
          city: undefined,
          address_components: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFilters = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/search-filters`,
        "PUT",
        {},
        {
          include: includedBentoSearchFilterIds,
          exclude: excludedBentoSearchFilterIds,
        },
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  unstable_usePrompt({
    message:
      "You have unsaved changes. Are you sure you want to navigate away from this page?",
    when: ({ currentLocation, nextLocation }) =>
      Boolean(isDirty) && currentLocation.pathname !== nextLocation.pathname,
  });

  const hasDifference = () => {
    if (!profile || Object.keys(onboardingForm)?.length === 0) return;
    for (const key in profile) {
      if (
        key in onboardingForm &&
        JSON.stringify(profile[key as keyof OrganizationProfile]) !==
          JSON.stringify(onboardingForm[key])
      ) {
        return true;
      }
    }
    return false;
  };

  const updateCategory = async (categories: BentoCategory[]) => {
    try {
      const { categories: updatedCategories } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/categories`,
        "PUT",
        {},
        {
          categoryIds: categories?.map((x) => x.id),
        },
      );
      setCurrentOrg((prev) => {
        const copy = makeDeepCopy(prev);
        copy.bentoCategories = updatedCategories;
        return copy;
      });
      setNeedsUpdateCategory(false);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const validateRequiredFields = (form: Map = onboardingForm) => {
    const requiredKeys: Map = {
      name: "Name",
      addressComponents: "Location",
      preferredBrandSize: "Preferred Brand Size",
      introduction: "About Me",
    };
    const missingKeys = [];
    for (const key in requiredKeys) {
      const value = form[key];
      if (
        value?.length === 0 ||
        // @ts-ignore: Guaranteed type is string
        (typeof value === "string" && value?.trim()?.length === 0)
      ) {
        missingKeys.push(requiredKeys[key]);
      }
    }
    return missingKeys;
  };

  useEffect(() => {
    if (!isDirty && hasDifference()) {
      setIsDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingForm]);

  useEffect(() => {
    if (profile?.id) {
      setOnboardingForm({
        ...profile,
        name: currentUser?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <Box sx={styles.page}>
      <AboutForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        handleNext={saveOnboardingForm}
        hideDiscoveryQuestion
        locationAutocompleteRef={locationAutocompleteRef}
      />
      {onboardingForm?.id && (
        <IncomeForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
          handleNext={saveOnboardingForm}
        />
      )}

      <Box sx={{ px: 3 }}>
        <DetailedCategoryForm
          setNeedsUpdateCategory={setNeedsUpdateCategory}
          topLevelCategories={topLevelCategories}
        />
        <Introduction
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
          saveOnboardingForm={saveOnboardingForm}
        />
        <PartnershipForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
        />
        <EngagementForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
        />
        <StandOutForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
        />
        <SignatureForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
          saveOnboardingForm={saveOnboardingForm}
        />
        <RecommendationForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
          saveOnboardingForm={saveOnboardingForm}
          setExcludeIncludeFilterHasChanged={setExcludeIncludeFilterHasChanged}
        />
      </Box>

      <Box sx={styles.button(tabWidth, discoverTab !== null)}>
        <Fab
          disabled={loading}
          variant="extended"
          color="primary"
          onClick={saveOnboardingForm}
        >
          <Box component="i" className="fa-regular fa-save" sx={{ mr: 1 }} />
          {loading ? <CircularProgress size={20} /> : "Save"}
        </Fab>
      </Box>
    </Box>
  );
};

export default Profile;
